import { lazy, Suspense, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { useTracker } from '@archipro-website/tracker';
import type { LibraryDiagram } from '@modules/product/type/product-gallery-types';
import {
    detailCta,
    linkifyHtml,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import { CarouselPaginationPill } from '~/ui-rocco/pages/home-page/components/CarouselPaginationPill';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { photoViewerTargetAtom } from '../../photo-viewer';

/** Lazy-loaded photo viewer — keeps the heavy lightbox out of the page chunk. */
const PhotoViewerModal = lazy(
    () => import('@modules/product/component/photo-gallery/PhotoViewerModal'),
);

export interface SpecsDimensionsRowContentProps {
    /** Dimension diagrams for the left column (carousel + photo viewer). */
    diagrams: LibraryDiagram[];
    /** Product id + title for the photo viewer + tracking. */
    productId: number;
    productTitle: string;
    /** Professional name for the no-diagram placeholder copy. */
    professionalTitle: string;
    /** Opens the enquiry modal (no-diagram placeholder + photo viewer CTA). */
    onEnquire: () => void;
    /** Size titles, newline-joined so each renders on its own line. */
    dimensions?: string;
    /** The CMS "Specifications" tab content (HTML — usually a bullet list). */
    specsHtml?: string | null;
    /** PerformanceDescription HTML. */
    performanceHtml?: string | null;
    /** WarrantyDuration text. */
    warranty?: string;
}

/** Sizes for the 746px (at the 1920 anchor) square diagram. */
const DIAGRAM_SIZES = getImagesSizes({ desktop: 746, mobile: 366 });

// Same label/value pair as the Product Details table above: the two rows sit
// in one accordion and read as one table, so the label column, the right-
// aligned grey value and the row size all match it rather than keeping the
// page-width 22px scale.
const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] text-black max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1rem]';
const VALUE_CLASS =
    'm-0 min-w-0 flex-1 break-words text-right font-sans font-normal leading-[1.3] text-[#767676] max-md:text-sm md:text-[1rem]';
const VALUE_HTML_CLASS = cn(
    VALUE_CLASS,
    RICH_TEXT_HTML_CLASS,
    // The shared rich-text headings carry their own larger sizes; inside the
    // table every line sits at the row size.
    '[&_h2]:text-[1rem] [&_h3]:text-[1rem] [&_h4]:text-[1rem]',
    // A right-aligned value cannot carry bullets: the marker sits at the
    // left of the column, an entire gap away from the text it belongs to.
    // The lines read as a list from their own stacking.
    '[&_ol]:my-0 [&_ol]:list-none [&_ol]:pl-0 [&_ul]:my-0 [&_ul]:list-none [&_ul]:pl-0',
    '[&_table]:w-full [&_td]:py-1',
);

/** The shared rocco pagination pill (chevrons + dots) below the diagram. */
const DiagramPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center pt-4 max-md:pt-3">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Product diagrams"
            />
        </div>
    );
};

/** Empty-state icon — a drawing/diagram sheet with a "?" magnifier. */
const NoDiagramIcon = () => (
    <svg
        width="122"
        height="100"
        viewBox="0 0 122 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M110 88C115.523 88 120 83.5228 120 78C120 72.4772 115.523 68 110 68C104.477 68 100 72.4772 100 78C100 83.5228 104.477 88 110 88ZM110 90C116.627 90 122 84.6274 122 78C122 71.3726 116.627 66 110 66C103.373 66 98 71.3726 98 78C98 84.6274 103.373 90 110 90Z"
            fill="#9B9B9B"
        />
        <text
            x="110"
            y="82.5"
            textAnchor="middle"
            fontSize="13"
            fontWeight="600"
            fontFamily="sans-serif"
            fill="#9B9B9B"
        >
            ?
        </text>
        <path
            d="M21 2H101C105.418 2 109 5.58172 109 10V67H111V10C111 4.47715 106.523 0 101 0H21C15.4772 0 11 4.47716 11 10V90C11 95.5229 15.4772 100 21 100H101C106.523 100 111 95.5228 111 90V89H109V90C109 94.4183 105.418 98 101 98H21C16.5817 98 13 94.4183 13 90V10C13 5.58172 16.5817 2 21 2Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M33 63H39V51.8003C39 51.7019 38.9581 51.5357 38.7381 51.3508C38.5134 51.1621 38.1416 51 37.6667 51H34.3333C33.8584 51 33.4866 51.1621 33.2619 51.3508C33.0419 51.5357 33 51.7019 33 51.8003V63ZM41 65V51.8003C41 50.2519 39.5098 49 37.6667 49H34.3333C32.4902 49 31 50.2519 31 51.8003V63.5285C31 64.3411 31.7843 65 32.7516 65H41Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M98 63V51.8003C98 51.7019 97.9581 51.5357 97.7381 51.3508C97.5134 51.1621 97.1416 51 96.6667 51H93.3333C92.8584 51 92.4866 51.1621 92.2619 51.3508C92.0419 51.5357 92 51.7019 92 51.8003V63H98ZM98.2484 65C99.2157 65 100 64.3411 100 63.5285V51.8003C100 50.2519 98.5098 49 96.6667 49H93.3333C91.4902 49 90 50.2519 90 51.8003V65H98.2484Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M41 63H90V47.9625C90 45.7341 88.2418 44 86.1632 44H44.8368C42.7582 44 41 45.7341 41 47.9625V63ZM92 65V47.9625C92 44.67 89.3863 42 86.1632 42H44.8368C41.6137 42 39 44.67 39 47.9625V65H92Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M64 63V44H66V63H64Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M90 58H41V56H90V58Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M36 37L31 34L36 31V33H93V31L98 34L93 37V35H36V37Z"
            fill="#9B9B9B"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M25 60L22 65L19 60H21V47H19L22 42L25 47H23V60H25Z"
            fill="#9B9B9B"
        />
    </svg>
);

interface SpecRow {
    label: string;
    text?: string;
    html?: string | null;
}

/**
 * (02) Specs & Dimensions — per Figma 1846:9046: the dimension diagram(s) on the
 * left as a clear-cut carousel (grey fill + multiply like the hero gallery, an
 * (n/total) badge, prev/next, and a tap to open the diagram full screen — the
 * legacy lightbox on desktop, the mobile viewer's Diagram tab on mobile), and
 * a left-aligned spec table on the right folding together Dimensions, the CMS
 * "Specifications" tab, Performance and Warranty. With no diagram the left slot
 * is a same-size placeholder inviting an enquiry.
 */
export const SpecsDimensionsRowContent = ({
    diagrams,
    productId,
    productTitle,
    onEnquire,
    dimensions,
    specsHtml,
    performanceHtml,
    warranty,
}: SpecsDimensionsRowContentProps) => {
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const openMobileViewer = useSetAtom(photoViewerTargetAtom);
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const specRows: SpecRow[] = [
        { label: 'Dimensions', text: dimensions },
        { label: 'Specs', html: specsHtml },
        { label: 'Performance', html: performanceHtml },
        { label: 'Warranty', text: warranty },
    ].filter((row) => row.text || row.html);

    const hasDiagrams = diagrams.length > 0;

    const trackViewerOpen = () => {
        tracker.log('PhotoViewerTrigger', {
            url: new URL(window.location.href),
            data: { LibraryItemID: productId || 0 },
        });
    };

    const openViewerAt = (index: number) => {
        trackViewerOpen();
        setActiveIndex(index);
        setViewerOpen(true);
    };

    /** Mobile opens the shared full-bleed viewer on its Diagram tab. */
    const openMobileViewerAt = (slideId: number) => {
        trackViewerOpen();
        openMobileViewer({ tab: 'diagram', slideId });
    };

    const renderDiagram = (diagram: LibraryDiagram) => (
        // Clear-cut treatment like the hero gallery: contained on the grey fill,
        // multiplying into it.
        <Image
            src={diagram.LibraryImageFile?.DetailPageSquare?.Desktop ?? ''}
            alt={productTitle}
            fill
            fit="contain"
            multiplied={Boolean(diagram.IsMultiplied)}
            sizes={DIAGRAM_SIZES}
            fadeIn={false}
        />
    );

    return (
        <div className="pb-[1.8rem] max-md:pb-4">
            <div className="grid max-md:gap-y-6 md:grid-cols-[46.625rem_1fr] md:gap-x-[14.6875rem]">
                {hasDiagrams ? (
                    <div className="self-start">
                        <Carousel
                            setApi={setEmblaApi}
                            options={{
                                align: 'start',
                                containScroll: 'trimSnaps',
                                loop: diagrams.length > 1,
                            }}
                        >
                            <CarouselContent>
                                {diagrams.map((diagram, index) => (
                                    <CarouselItem
                                        key={`${diagram.ID}-${index}`}
                                        className="basis-full"
                                    >
                                        {mobile ? (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openMobileViewerAt(
                                                        diagram.ID,
                                                    )
                                                }
                                                aria-label={`View ${productTitle} diagram ${index + 1} full screen`}
                                                className="relative block aspect-square w-full overflow-hidden bg-[var(--color-clearcut-fill)]"
                                            >
                                                {renderDiagram(diagram)}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openViewerAt(index)
                                                }
                                                aria-label={`View ${productTitle} diagram ${index + 1} full screen`}
                                                className="relative block aspect-square w-full cursor-zoom-in overflow-hidden bg-[var(--color-clearcut-fill)]"
                                            >
                                                {renderDiagram(diagram)}
                                            </button>
                                        )}
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <DiagramPagination />
                        </Carousel>
                    </div>
                ) : (
                    // A third the height of the diagram it stands in for, at
                    // the same width: an empty state has an icon, a line and a
                    // button in it, and a full square of grey around that reads
                    // as a missing image rather than as a prompt. `min-h` keeps
                    // the content off the edges if the column ever narrows
                    // enough that a 3:1 box would clip it.
                    <div className="flex aspect-[3/1] min-h-[14rem] w-full flex-col items-center justify-center gap-[1rem] self-start bg-[#f7f7f7] px-[3rem] py-8 text-center max-md:aspect-[2/1] max-md:gap-4 max-md:px-6">
                        <NoDiagramIcon />
                        <span className="font-sans leading-[1.3] font-normal text-[#767676] max-md:text-xs md:text-[1.125rem]">
                            Looking for a dimension diagram?
                        </span>
                        <button
                            type="button"
                            onClick={onEnquire}
                            className={detailCta(
                                'filled-light',
                                'h-[2.75rem] max-w-full overflow-hidden px-6 text-ellipsis whitespace-nowrap text-sm md:h-[3.25rem] md:text-base',
                            )}
                        >
                            Enquire now
                        </button>
                    </div>
                )}

                <dl className="m-0 self-start">
                    {specRows.map((row) => (
                        <div
                            key={row.label}
                            className="flex items-start border-b border-black/10 py-[0.8125rem] first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem]"
                        >
                            <dt className={LABEL_CLASS}>{row.label}</dt>
                            {row.html ? (
                                <dd
                                    className={VALUE_HTML_CLASS}
                                    dangerouslySetInnerHTML={{
                                        __html: linkifyHtml(row.html),
                                    }}
                                />
                            ) : (
                                // Plain-text values keep author line breaks
                                // (Dimensions renders one size per line).
                                <dd
                                    className={cn(
                                        VALUE_CLASS,
                                        'whitespace-pre-line',
                                    )}
                                >
                                    {row.text}
                                </dd>
                            )}
                        </div>
                    ))}
                </dl>
            </div>

            {viewerOpen ? (
                <Suspense fallback={null}>
                    <PhotoViewerModal
                        key={productId}
                        stack={diagrams}
                        selectedSlide={activeIndex}
                        open={viewerOpen}
                        toggle={() => setViewerOpen((current) => !current)}
                        itemID={productId}
                        title={productTitle}
                        cta={{
                            onEnquire,
                            enquiryLabel: 'Enquire',
                        }}
                        onSlideChange={(stackIndex) => {
                            setActiveIndex(stackIndex);
                            emblaApi?.scrollTo(stackIndex);
                        }}
                    />
                </Suspense>
            ) : null}
        </div>
    );
};
